/* eslint-disable no-unused-vars */
import { computed, defineComponent, h, inject, provide, reactive, ref } from 'vue'

const ROUTER_KEY = Symbol('router')
const ROUTE_KEY = Symbol('route')
const VIEW_DEPTH_KEY = Symbol('view-depth')

function flattenRoutes(routes, parents = []) {
  const records = []
  routes.forEach((route) => {
    const currentParents = [...parents, route]
    if (route.children && route.children.length) {
      records.push(...flattenRoutes(route.children, currentParents))
      return
    }
    records.push({
      path: route.path,
      name: route.name, // 必须保存 name，供守卫逻辑判断
      redirect: route.redirect,
      componentChain: currentParents.filter((item) => item.component).map((item) => item.component),
      meta: route.meta || {}
    })
  })
  return records
}

function normalizePath(path) {
  if (!path) return '/'
  return path.startsWith('/') ? path : `/${path}`
}

export function createWebHistory() { return { type: 'web' } }

export function createRouter({ routes = [] }) {
  const records = flattenRoutes(routes)
  const byPath = new Map()
  const byName = new Map()
  records.forEach((record) => {
    byPath.set(normalizePath(record.path), record)
    if (record.name) byName.set(record.name, record)
  })

  const currentPath = ref(normalizePath(window.location.pathname || '/'))
  const currentRoute = reactive({ path: '/', name: '', meta: {} })
  const beforeGuards = [] // 存储保安逻辑

  const syncRoute = (path) => {
    const normalized = normalizePath(path)
    let record = byPath.get(normalized)
    
    // 处理重定向 (如 / 重定向到 /remove)
    if (record?.redirect) {
      const target = normalizePath(record.redirect)
      window.history.replaceState({}, '', target)
      currentPath.value = target
      record = byPath.get(target)
    }

    if (!record) record = records[0]

    currentRoute.path = currentPath.value
    currentRoute.name = record?.name || ''
    currentRoute.meta = record?.meta || {}
  }

  // 执行保安检查的逻辑
  const runGuards = (targetPath, callback) => {
    const record = byPath.get(normalizePath(targetPath)) || records[0]
    const to = { name: record.name, path: targetPath, meta: record.meta }
    const from = { ...currentRoute }

    const next = (nextTarget) => {
      if (!nextTarget) {
        callback() // 准许通行
      } else {
        const nextPath = typeof nextTarget === 'string' ? nextTarget : (byName.get(nextTarget.name)?.path || '/')
        push(nextPath, true) // 强制跳转
      }
    }

    if (beforeGuards.length) {
      beforeGuards[0](to, from, next)
    } else {
      callback()
    }
  }

  const push = (targetPath, isGuardRedirect = false) => {
    const target = normalizePath(targetPath)
    
    // 如果不是由守卫触发的重定向，则需要先跑一遍守卫
    if (!isGuardRedirect) {
      runGuards(target, () => {
        window.history.pushState({}, '', target)
        currentPath.value = target
        syncRoute(target)
      })
    } else {
      window.history.replaceState({}, '', target)
      currentPath.value = target
      syncRoute(target)
    }
  }

  const router = {
    currentPath,
    currentRoute,
    push,
    // 补全缺失的 beforeEach 方法
    beforeEach: (fn) => { beforeGuards.push(fn) },
    install: (app) => {
      window.addEventListener('popstate', () => {
        const path = normalizePath(window.location.pathname || '/')
        runGuards(path, () => {
          currentPath.value = path
          syncRoute(path)
        })
      })

      app.component('RouterView', RouterView)
      app.provide(ROUTER_KEY, router)
      app.provide(ROUTE_KEY, currentRoute)

      // ⚡ 核心修复：初始加载时立即运行保安检查
      const initialPath = normalizePath(window.location.pathname || '/')
      runGuards(initialPath, () => {
        syncRoute(initialPath)
      })
    }
  }

  const RouterView = defineComponent({
    name: 'RouterView',
    setup() {
      const depth = inject(VIEW_DEPTH_KEY, 0)
      const targetComponent = computed(() => {
        const record = byPath.get(normalizePath(router.currentPath.value)) || records[0]
        return record?.componentChain[depth] || null
      })
      provide(VIEW_DEPTH_KEY, depth + 1)
      return () => targetComponent.value ? h(targetComponent.value) : null
    }
  })

  return router
}

export function useRouter() { return inject(ROUTER_KEY) }
export function useRoute() { return inject(ROUTE_KEY) }
